import { forwardRef, useMemo } from 'react';
import { NumericFormat, useNumericFormat } from 'react-number-format';

import { InputIcon } from '@perpay-web/components/base//InputIcon/InputIcon';
import { InputError } from '@perpay-web/components/base/InputError/InputError';
import { InputLabel } from '@perpay-web/components/base/InputLabel/InputLabel';
import { InputPrefix } from '@perpay-web/components/base/InputPrefix/InputPrefix';
import { useEndToEndTestId } from '@perpay-web/hooks/useEndToEndTestId';
import { getClassName } from '@perpay-web/utils/domUtils';

import styles from '@perpay-web/design/styles/inputs.scss';

const InputSuffix = ({ suffixComponent }) => suffixComponent || null;

export const TextInputNumeric = forwardRef((props, ref) => {
    const {
        value = '',
        name,
        className = '',
        label,
        touched = false,
        errors = null,
        loading = false,
        prefix,
        suffixComponent,
        mask = 'x',
        format,
        icon = null,
        autoComplete = 'off',
        ...rest
    } = props;
    const { format: baseFormat, removeFormatting } = useNumericFormat(props);
    const endToEndTestIDParams = useEndToEndTestId();

    const numericFormatter = useMemo(() => {
        if (typeof format === 'function') {
            return (val) => {
                const formattedVal = baseFormat(val);
                return format(formattedVal);
            };
        }
        return format;
    }, [baseFormat, format]);

    const isError = useMemo(
        () => errors && touched && errors[0],
        [errors, touched],
    );

    return (
        <div
            className={getClassName(
                styles['text-field-row'],
                isError && styles['text-field-row--error'],
                className,
            )}
        >
            <div className={getClassName(styles['text-field-container'])}>
                <NumericFormat
                    getInputRef={ref}
                    value={value}
                    name={name}
                    mask={mask}
                    format={numericFormatter}
                    removeFormatting={removeFormatting}
                    autoComplete={autoComplete}
                    data-lpignore='true'
                    {...endToEndTestIDParams}
                    {...rest}
                    className={getClassName(
                        styles['text-field'],
                        (value || value === 0) && styles['text-field--filled'],
                        label && styles['text-field--labeled'],
                    )}
                />
                <InputLabel label={label} />
                <InputPrefix prefix={prefix} />
                <InputIcon icon={icon} loading={loading} />
                <InputSuffix suffixComponent={suffixComponent} />
            </div>
            {isError ? <InputError name={name} error={errors[0]} /> : null}
        </div>
    );
});
